import React, {useState, useEffect, useMemo, useRef} from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, TimeScale, ChartOptions, ChartData, Plugin, TooltipItem } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { getTimeSeriesData, getAnnotationPeriods } from '../../services/api.service';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, TimeScale);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}

interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        total_count: number;
    };
}

interface DailyDataPoint {
    date: string;
    battery: number | null;
    solar: number | null;
}

interface BatteryPeriod {
    start: string;
    end: string;
    text: string;
}

interface BatterySolarChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
    showSevereBatteryIssues?: boolean;
     showImminentBatteryFailure?: boolean;
}

const BatterySolarChart: React.FC<BatterySolarChartProps> = ({
    stationId,
    startDate,
    endDate,
    height = 400,
    showSevereBatteryIssues = false,
     showImminentBatteryFailure = false
}) => {
    // State management
    const [batteryData, setBatteryData] = useState<TimeSeriesData | null>(null);
    const [solarData, setSolarData] = useState<TimeSeriesData | null>(null);
    const [severeBatteryPeriods, setSevereBatteryPeriods] = useState<BatteryPeriod[]>([]);
    const [severeBatteryPeriodsCache, setSevereBatteryPeriodsCache] = useState<BatteryPeriod[]>([]);
    const [imminentFailurePeriods, setImminentFailurePeriods] = useState<BatteryPeriod[]>([]);
    const [imminentFailurePeriodsCache, setImminentFailurePeriodsCache] = useState<BatteryPeriod[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [chartKey, setChartKey] = useState<number>(0); // Add key to force chart re-render

     const chartRef = useRef<any>(null);

       // Reset zoom function that properly accesses the chart instance
    const resetZoom = () => {
        if (chartRef.current) {
            // Access the chart instance through the react-chartjs-2 wrapper
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };

    const adjustedStartDate = useMemo(() =>
        moment(startDate).subtract(6, 'month').format('YYYY-MM-DDTHH:mm:ss'),
        [startDate]
    );

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch both datasets in parallel
                const [batteryResponse, solarResponse] = await Promise.allSettled([
                    getTimeSeriesData(stationId, 'lb', adjustedStartDate, endDate),
                    getTimeSeriesData(stationId, 'ra', adjustedStartDate, endDate)
                ]);

                // Handle battery data response
                if (batteryResponse.status === 'fulfilled') {
                    setBatteryData(batteryResponse.value);
                } else {
                    console.error('Failed to fetch battery data:', batteryResponse.reason);
                }

                // Handle solar data response
                if (solarResponse.status === 'fulfilled') {
                    setSolarData(solarResponse.value);
                } else {
                    console.error('Failed to fetch solar data:', solarResponse.reason);
                }

            } catch (err) {
                setError('Failed to fetch battery and solar data');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [stationId, adjustedStartDate, endDate]);

        // Separate effect for severe battery periods
    useEffect(() => {
        const fetchSevereBatteryPeriods = async () => {
            try {
                const severeBatteryResponse = await getAnnotationPeriods(stationId, 'severe_battery_issues', startDate, endDate);
                const periods = severeBatteryResponse.periods || [];

                // Always cache the fetched data
                setSevereBatteryPeriodsCache(periods);

                // Only display if toggle is enabled
                if (showSevereBatteryIssues) {
                    setSevereBatteryPeriods(periods);
                } else {
                    setSevereBatteryPeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch severe battery periods:', err);
                setSevereBatteryPeriodsCache([]);
                setSevereBatteryPeriods([]);
            }
        };

        fetchSevereBatteryPeriods();
    }, [stationId, startDate, endDate, showSevereBatteryIssues]);

    // Separate effect for imminent failure periods
    useEffect(() => {
        const fetchImminentFailurePeriods = async () => {
            try {
                const imminentFailureResponse = await getAnnotationPeriods(stationId, 'imminent_battery_failure', startDate, endDate);
                const periods = imminentFailureResponse.periods || [];

                // Always cache the fetched data
                setImminentFailurePeriodsCache(periods);

                // Only display if toggle is enabled
                if (showImminentBatteryFailure) {
                    setImminentFailurePeriods(periods);
                } else {
                    setImminentFailurePeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch imminent failure periods:', err);
                setImminentFailurePeriodsCache([]);
                setImminentFailurePeriods([]);
            }
        };

        fetchImminentFailurePeriods();
    }, [stationId, startDate, endDate, showImminentBatteryFailure]);


        // Effect to handle toggle changes using cached data
    useEffect(() => {
        if (showSevereBatteryIssues) {
            setSevereBatteryPeriods(severeBatteryPeriodsCache);
        } else {
            setSevereBatteryPeriods([]);
        }
        setChartKey(prev => prev + 1);
    }, [showSevereBatteryIssues, severeBatteryPeriodsCache]);

    useEffect(() => {
    if (showImminentBatteryFailure) {
        setImminentFailurePeriods(imminentFailurePeriodsCache);
    } else {
        setImminentFailurePeriods([]);
    }
    setChartKey(prev => prev + 1);
}, [showImminentBatteryFailure, imminentFailurePeriodsCache]);


    // Create annotation plugin for battery issues
    const batteryAnnotationPlugin: Plugin<'line'> = useMemo(() => ({
        id: 'batteryAnnotation',
        beforeDraw: (chart: any) => {
            if (!showSevereBatteryIssues || !severeBatteryPeriods.length) return;

            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            const xScale = chart.scales.x;

              // Draw severe battery issues (red)
            if (showSevereBatteryIssues && severeBatteryPeriods.length) {
                severeBatteryPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    if (!isNaN(startX) && !isNaN(endX)) {
                        const width = Math.max(Math.abs(endX - startX), 10);
                        const actualStartX = Math.max(Math.min(startX, endX), chartArea.left);

                        // Draw background rectangle (red)
                        ctx.save();
                        ctx.fillStyle = 'rgba(220, 53, 69, 0.15)';
                        ctx.fillRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                        // Draw border (red)
                        ctx.strokeStyle = '#dc3545';
                        ctx.lineWidth = 2;
                        ctx.setLineDash([5, 5]);
                        ctx.strokeRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                        // Draw label
                        ctx.setLineDash([]);
                        ctx.fillStyle = '#721c24';
                        ctx.font = 'bold 11px Arial';
                        ctx.textAlign = 'center';
                        const textX = actualStartX + width / 2;

                        // Draw background for text
                        const labelText = period.text || 'Severe Battery Issues';
                        const textWidth = ctx.measureText(labelText).width;
                        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                        ctx.fillRect(textX - textWidth/2 - 4, chartArea.top + 5, textWidth + 8, 16);

                        // Draw border around text background
                        ctx.strokeStyle = '#dc3545';
                        ctx.lineWidth = 1;
                        ctx.strokeRect(textX - textWidth/2 - 4, chartArea.top + 5, textWidth + 8, 16);

                        // Draw text
                        ctx.fillStyle = '#721c24';
                        ctx.fillText(labelText, textX, chartArea.top + 16);
                        ctx.restore();
                    }
                });
            }

             // Draw imminent failure periods (orange)
            if (showImminentBatteryFailure && imminentFailurePeriods.length) {
                imminentFailurePeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    if (!isNaN(startX) && !isNaN(endX)) {
                        const width = Math.max(Math.abs(endX - startX), 10);
                        const actualStartX = Math.max(Math.min(startX, endX), chartArea.left);

                        // Draw background rectangle (orange)
                        ctx.save();
                        ctx.fillStyle = 'rgba(255, 149, 0, 0.15)';
                        ctx.fillRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                        // Draw border (orange)
                        ctx.strokeStyle = '#ff9500';
                        ctx.lineWidth = 2;
                        ctx.setLineDash([10, 5]);
                        ctx.strokeRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                        // Draw label
                        ctx.setLineDash([]);
                        ctx.fillStyle = '#b8560e';
                        ctx.font = 'bold 11px Arial';
                        ctx.textAlign = 'center';
                        const textX = actualStartX + width / 2;

                        // Position text below severe battery issues if they overlap
                        const textY = showSevereBatteryIssues ? chartArea.top + 35 : chartArea.top + 16;

                        // Draw background for text
                        const labelText = period.text || 'Imminent Battery Failure';
                        const textWidth = ctx.measureText(labelText).width;
                        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                        ctx.fillRect(textX - textWidth/2 - 4, textY - 11, textWidth + 8, 16);

                        // Draw border around text background
                        ctx.strokeStyle = '#ff9500';
                        ctx.lineWidth = 1;
                        ctx.strokeRect(textX - textWidth/2 - 4, textY - 11, textWidth + 8, 16);

                        // Draw text
                        ctx.fillStyle = '#b8560e';
                        ctx.fillText(labelText, textX, textY);
                        ctx.restore();
                    }
                });
            }


        }
    }), [showSevereBatteryIssues, severeBatteryPeriods, showImminentBatteryFailure, imminentFailurePeriods]);


    // Chart data configuration
    const chartData: ChartData<'line'> = useMemo(() => {
         const datasets: any[] = [];

       if (batteryData?.data && batteryData.data.length > 0) {
            datasets.push({
                label: 'Battery Percentage (%)',
                data: batteryData.data.map(d => ({
                    x: moment(d.timestamp).valueOf(),
                    y: d.value  // Keep null values as null for gaps
                })),
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.1)',
                yAxisID: 'y-battery',
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4,
                spanGaps: false  // Show gaps where data is missing
            });
        }

       if (solarData?.data && solarData.data.length > 0) {
           datasets.push({
                label: 'Solar Radiation (W/m²)',
                data: solarData.data.map(d => ({
                    x: moment(d.timestamp).valueOf(),
                    y: d.value  // Keep null values as null for gaps
                })),
                    borderColor: '#ffc107',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    yAxisID: 'y-solar',
               tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4,
                spanGaps: false  // Show gaps where data is missing
           })
       }
        return { datasets };
    }, [batteryData, solarData]);

    // Chart options configuration
    const chartOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: 'Battery and Solar Radiation',
                font: {
                    size: 16
                }
            },
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        if (context.datasetIndex === 0) {
                            return `Battery: ${value?.toFixed(2) || 0} V`;
                        } else {
                            return `Solar Radiation: ${value?.toFixed(1) || 0} W/m²`;
                        }
                    }
                },
                afterBody: (tooltipItems: TooltipItem<'line'>[]) => {
                        // Add extra info for severe battery issue periods
                        const xValue = tooltipItems[0]?.parsed?.x;
                        if (xValue) {
                            const date = moment(xValue);
                            const affectedPeriod = severeBatteryPeriods.find(period =>
                                date.isBetween(moment(period.start), moment(period.end), 'day', '[]')
                            );
                            if (affectedPeriod) {
                                return ['', '⚠️ Severe Battery Issue Period'];
                            }
                        }
                        return [];
                    }

            },
              // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        day: 'DD/MM',
                        week: 'DD/MM',
                        month: 'MM'
                    },
                    tooltipFormat: 'DD MMM'
                },
                title: {
                    display: true,
                    text: 'Date'
                }
            },
            'y-battery': {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Battery Percentage (%)'
                },
                 min: 0,
                max: 100
            },
            'y-solar': {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Solar Radiation (W/m²)'
                },
                grid: {
                    drawOnChartArea: false
                },

            }
        },
        parsing: false,
        normalized: true,
        spanGaps: false
    }), [severeBatteryPeriods]);

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
                        <div className="text-muted">
                            <i className="fa fa-spinner fa-spin fa-3x mb-3"></i>
                            <p>Loading battery and solar radiation data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }


    // Main render
    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    {/*<h5 className="card-title mb-0">{title}</h5>*/}
                    <div className="d-flex align-items-center gap-3">
                         {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>

                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{ height: `${height}px` }}>
                    <Chart key={chartKey}
                        ref={chartRef} type="line" data={chartData} options={chartOptions}
                           plugins={[batteryAnnotationPlugin]}
                    />
                </div>
                <div className="mt-2">
                    <small className="text-muted">
                        Battery and solar radiation (daily averages)

                        {severeBatteryPeriods.length > 0 && (
                            <span className="ms-3 text-danger">
                                <i className="fa fa-exclamation-triangle"></i> Severe battery issues: {severeBatteryPeriods.length} periods
                            </span>
                        )}
                    </small>
                </div>
            </div>
        </div>
    );
};

export default BatterySolarChart;